/**
 * G6 layout presets. Each returns a config object ready for `graph.setLayout`.
 *
 * Defaults are tuned for biomedical knowledge graphs in the 30-100 node range:
 *  - enough repulsion that nodes never overlap
 *  - enough alpha decay that the simulation settles within a second
 *  - generous label clearance via large effective node sizes
 */

export type LayoutType = "radial" | "dagre" | "concentric" | "grid";

export const LAYOUT_LABELS: Record<LayoutType, string> = {
  radial: "Radial",
  dagre: "Hierarchical",
  concentric: "Concentric",
  grid: "Grid",
};

/** The default layout used on first render — radial reads best for medical KGs. */
export const DEFAULT_LAYOUT: LayoutType = "radial";

export const LAYOUT_OPTIONS = Object.entries(LAYOUT_LABELS).map(([value, label]) => ({
  value: value as LayoutType,
  label,
}));

/** Layouts that render reliably on mobile (hidden container, no animation loop).
 *  Dagre (hierarchical) is excluded because knowledge graphs have cycles
 *  and dagre is designed for DAGs — produces garbled output with cycles. */
export const MOBILE_LAYOUT_OPTIONS = LAYOUT_OPTIONS.filter((o) =>
  ["radial", "grid"].includes(o.value),
);

export function getLayoutConfig(type: LayoutType): Record<string, unknown> {
  switch (type) {
    case "radial":
      return {
        type: "radial",
        unitRadius: 140,
        preventOverlap: true,
        nodeSize: 80,
        maxPreventOverlapIteration: 240,
        focusNode: 0,
        linkDistance: 130,
        strictRadial: false,
      };
    case "dagre":
      return {
        type: "dagre",
        rankdir: "TB",
        nodesep: 50,
        ranksep: 70,
        align: "UL",
      };
    case "concentric":
      return {
        type: "concentric",
        preventOverlap: true,
        nodeSize: 80,
        minNodeSpacing: 36,
        equidistant: true,
      };
    case "grid":
      return {
        type: "grid",
        preventOverlap: true,
        nodeSize: 80,
        condense: false,
      };
  }
}
